import { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, Users, MessageSquare, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reports/overview`);
      setOverview(response.data);
    } catch (error) {
      toast.error('Failed to fetch overview data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, color }) => (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/ /g, '-')}`} className="bg-card border border-border p-6 hover:border-primary/50 transition-colors duration-300">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-muted-foreground text-sm font-body uppercase tracking-wider">{title}</p>
          <h3 className="text-4xl font-heading font-bold text-foreground mt-2">{value}</h3>
          {trend && (
            <p className={`text-sm mt-2 flex items-center gap-1 ${color}`}>
              <TrendingUp className="w-4 h-4" />
              {trend}
            </p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-md bg-${color || 'primary'}/10 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color || 'primary'}`} strokeWidth={2} />
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-8">
        <h1 className="text-4xl font-heading font-bold text-foreground">Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-card border border-border p-6 animate-pulse">
              <div className="h-24 bg-muted rounded"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground tracking-tight">DASHBOARD</h1>
        <p className="text-muted-foreground mt-2 font-body">Real-time coach performance monitoring</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Coaches"
          value={overview?.total_coaches || 0}
          icon={Users}
          color="text-primary"
        />
        <StatCard
          title="Active Groups"
          value={overview?.total_groups || 0}
          icon={MessageSquare}
          color="text-primary"
        />
        <StatCard
          title="Replied Today"
          value={overview?.today_replied || 0}
          icon={Activity}
          trend="+12% from yesterday"
          color="text-green-500"
        />
        <StatCard
          title="Pending"
          value={overview?.today_pending || 0}
          icon={Activity}
          color="text-yellow-500"
        />
      </div>

      <Card className="bg-card border border-border p-6">
        <h2 className="text-2xl font-heading font-bold text-foreground mb-4">Average Response Rate</h2>
        <div className="flex items-end gap-4">
          <div className="text-5xl font-heading font-bold text-primary">
            {overview?.average_response_rate || 0}%
          </div>
          <p className="text-muted-foreground mb-2">across all coaches today</p>
        </div>
      </Card>

      <Card className="bg-card border border-border p-6">
        <h2 className="text-2xl font-heading font-bold text-foreground mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            data-testid="generate-report-btn"
            onClick={async () => {
              try {
                await axios.post(`${BACKEND_URL}/api/reports/generate`);
                toast.success('Daily report generated successfully!');
                fetchOverview();
              } catch (error) {
                toast.error('Failed to generate report');
              }
            }}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-md font-body font-medium hover:brightness-110 active:scale-95 transition-transform duration-200"
          >
            Generate Today's Report
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;
